// One language, chosen from the phone.
//
// The audience is a hall full of people who did not install anything and will
// not look for a language switch: whatever their phone is set to is the best
// guess anyone has, and a second language printed underneath the first only
// makes both harder to read at a glance in the dark.
//
// Turkish or English, because those are the two the show is presented in. A
// phone set to anything else gets English, which is the safer default for a
// visitor.

export type Lang = "tr" | "en";

function pickLang(): Lang {
  const tags = navigator.languages?.length ? navigator.languages : [navigator.language ?? "en"];
  for (const tag of tags) {
    const base = String(tag).toLowerCase().split("-")[0];
    if (base === "tr") return "tr";
    if (base === "en") return "en";
  }
  return "en";
}

export const lang: Lang = pickLang();

const STRINGS = {
  tr: {
    statusLive:     "Canlı",
    statusConnect:  "Bağlanıyor",
    statusRetry:    "Yeniden bağlanıyor",
    gateScan:       "Koltuk kodunu okut",
    gateStarting:   "Kamera açılıyor…",
    gateScanning:   "Kodu çerçeveye getir",
    gateDenied:     "Kamera izni verilmedi. Telefonunun kendi kamera uygulaması da bu kodu okur.",
    gateInsecure:   "Bu bağlantıda kamera açılamıyor. Telefonunun kamera uygulamasıyla okut.",
    gateNoCam:      "Kamera bulunamadı. Telefonunun kamera uygulamasıyla okut.",
    // Not the same as a refusal, and iOS raises it often: another app or another
    // tab is holding the camera. "İzin verilmedi" sends people to Settings to
    // fix something that is not broken.
    gateBusy:       "Kamera başka bir uygulamada açık. Onu kapat ve tekrar dene.",
    gateWrong:      "Bu kod bu gösteriye ait değil. Koltuğundaki kodu okut.",
    // Somebody else is playing this seat. Said as a fact about the seat, not as
    // an accusation and not as an error: the usual cause is two people holding
    // the same code, and the person reading this is as likely as not the one
    // who scanned the wrong chair.
    seatBusy:       "Bu koltuk şu anda çalınıyor.",
    seatBusyHint:   "Aynı kodu iki telefon okutmuş olabilir. Kendi koltuğundaki kodu okut ya da birazdan tekrar dene.",
    seatRetry:      "Tekrar dene",
    seatRescan:     "Başka bir kod okut",
    // Removed by whoever is running the show. Deliberately plain and without a
    // reason: the operator has one and this screen is not the place to argue it.
    // There is no button, because there is nothing that would work — the block is
    // on this phone and on the code it arrived with.
    removedTitle:   "Bu telefon gösteriden çıkarıldı.",
    removedHint:    "Devam etmek istersen görevlilerden biriyle konuş.",
    tabTitle:       "Gösteri başka bir sekmede açık.",
    tabHint:        "Her telefon tek yerden çalabilir. Diğer sekmeye geç ya da gösteriyi buraya al.",
    tabRetake:      "Gösteriyi buraya al",
    // Before the show. This is the screen most of the audience will hold for
    // twenty minutes, so it says the one thing they want to know and then gets
    // out of the way. No countdown: a clock that is wrong in a hall is worse
    // than no clock, and nobody here knows when the band is actually ready.
    showSoonTitle:  "Gösteri birazdan başlıyor.",
    showSoonSub:    "Telefonun hazır. Başladığında burası kendiliğinden açılacak.",
    // ── the door ──────────────────────────────────────────────────────────
    // The code was scanned and it did not carry a seat. Everything here is read
    // once, in a dark hall, by somebody who wants to start playing — so it says
    // what to do rather than what happened.
    entryWorking:   "Bir saniye…",
    entryZoneTitle: "Neredesin?",
    entryZoneSub:   "Bulunduğun bölümü seç",
    entrySeatTitle: "Koltuk numaran",
    entrySeatSub:   "Koltuğunun üzerinde yazıyor",
    entrySeatGo:    "Devam",
    entrySeatRange: "Bu bölümde koltuklar {from}–{to} arası",
    entrySeatBad:   "Bu bölümde öyle bir koltuk yok. {from} ile {to} arasında bir numara gir.",
    entrySeatTaken: "O koltukta biri oynuyor. Kendi koltuğunun numarasını gir.",
    entryFullTitle: "Salon doldu",
    entryFullSub:   "Şu an için herkes içeride. Biraz sonra tekrar dene.",
    entryWatchTitle:"Salon doldu",
    entryWatchSub:  "İçeri alındın ama şu an gönderim kapalı — gösteriyi buradan izleyebilirsin.",
    entryWatchGo:   "Devam et",
    entryStale:     "Bu kod bu geceye ait değil. Koltuğundaki kodu okut.",
    entryGone:      "Bu bölüm artık yok. Koltuğundaki kodu okut.",
    entryClosed:    "Giriş şu an kapalı.",
    entryClosedTitle: "Giriş şu an kapalı",
    entryClosedSub:  "Yönlendiriliyorsun…",
    // Everybody arrived at once and the door is shedding. Not an error and not
    // this phone's fault: it says what is happening and that nothing is asked of
    // them, because the alternative is somebody pressing reload into a queue.
    entryBusyTitle: "Kapıda sıra var",
    entryBusy:      "Herkes aynı anda giriyor, sıranı bekliyoruz…",
    entryBusySub:   "Kapıdaki yoğunluk sürüyor. Birkaç saniye sonra tekrar dene.",
    entryOfflineTitle: "Bağlanılamadı",
    entryOffline:   "Bu kod bir koltuğa çevrilmeden bağlanamıyor ve şu an sunucuya ulaşılamıyor. Tekrar dene.",
    entryRetry:     "Tekrar dene",
    ctaTitle:       "Bilgi Al",
    ctaSub:         "Cosmic Symphony hakkında",
  },
  en: {
    statusLive:     "Live",
    statusConnect:  "Connecting",
    statusRetry:    "Reconnecting",
    gateScan:       "Scan your seat code",
    gateStarting:   "Opening the camera…",
    gateScanning:   "Hold the code inside the frame",
    gateDenied:     "Camera permission was declined. Your phone's own camera app reads this code too.",
    gateInsecure:   "The camera cannot open on this connection. Use your phone's camera app.",
    gateNoCam:      "No camera found. Use your phone's camera app.",
    gateBusy:       "The camera is open in another app. Close it and try again.",
    gateWrong:      "That code is not from this show. Scan the one on your seat.",
    seatBusy:       "Someone is already playing this seat.",
    seatBusyHint:   "Two phones may be holding the same code. Scan the one on your own seat, or try again in a moment.",
    seatRetry:      "Try again",
    seatRescan:     "Scan a different code",
    removedTitle:   "This phone has been removed from the show.",
    removedHint:    "Speak to a member of staff if you would like to take part again.",
    tabTitle:       "The show is open in another tab.",
    tabHint:        "One phone plays from one place. Switch to the other tab, or bring the show here.",
    tabRetake:      "Bring the show here",
    showSoonTitle:  "The show will begin soon.",
    showSoonSub:    "Your phone is ready. This screen opens on its own when it starts.",
    // ── the door ──────────────────────────────────────────────────────────
    entryWorking:   "One moment…",
    entryZoneTitle: "Where are you?",
    entryZoneSub:   "Choose the block you are in",
    entrySeatTitle: "Your seat number",
    entrySeatSub:   "It is printed on your seat",
    entrySeatGo:    "Continue",
    entrySeatRange: "Seats here run {from}–{to}",
    entrySeatBad:   "There is no such seat in this block. Enter a number between {from} and {to}.",
    entrySeatTaken: "Someone is playing that seat. Enter the number on your own seat.",
    entryFullTitle: "The house is full",
    entryFullSub:   "Everyone is in for the moment. Try again shortly.",
    entryWatchTitle:"The house is full",
    entryWatchSub:  "You are in, but sending is off right now — you can watch the show from here.",
    entryWatchGo:   "Continue",
    entryStale:     "That code is not from tonight. Scan the one on your seat.",
    entryGone:      "That block is gone. Scan the code on your seat.",
    entryClosed:    "The door is closed right now.",
    entryClosedTitle: "Entry is closed",
    entryClosedSub:  "Taking you there…",
    entryBusyTitle: "There is a queue",
    entryBusy:      "Everybody is arriving at once — waiting for your turn…",
    entryBusySub:   "The door is still busy. Give it a few seconds and try again.",
    entryOfflineTitle: "Cannot reach the show",
    entryOffline:   "This code has to be exchanged for a seat and the server is not answering. Try again.",
    entryRetry:     "Try again",
    ctaTitle:       "About the show",
    ctaSub:         "Discover Cosmic Symphony",
  },
} as const;

export type StringKey = keyof (typeof STRINGS)["en"];

export function t(key: StringKey): string {
  return STRINGS[lang][key];
}

// Screen readers and the browser's own text handling both go by this, and it is
// wrong on every page that ships a fixed lang attribute in its markup.
export function applyLangAttribute(): void {
  document.documentElement.setAttribute("lang", lang);
}
